import { useCallback, useEffect, useMemo, useState } from 'react';
import LoadingWrapper from 'components/LoadingWrapper';
import dayjs, { type Dayjs } from 'dayjs';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
import { calendarClient } from 'services/calendarClient';
import { getErrorMessage } from 'utils/errorUtils';
import { errorNotification } from 'utils/notifications';
import type {
  Log,
  LogQueryParams,
  LogStatus,
} from '@timeedit/calendar-integration-api-contract';
import {
  type Column,
  DefaultContentLayout,
  EditableTable,
  Icon,
  TEInput,
  Tag,
} from '@timeedit/ui-components';
import {
  DatePicker,
  Space,
  type TablePaginationConfig,
  Typography,
} from '@timeedit/ui-components/antd';
import RenderIcon from '../../../userManagement/logs/RenderIcon';
import { LogDetails } from './LogDetails';

export function MS365LogsPage() {
  const [curPage, setCurPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([null, null]);
  const [logs, setLogs] = useState<Log[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchMS365Logs = useCallback(
    async ({
      page = 1,
      limit = 10,
      startTime,
      endTime,
      searchText,
    }: LogQueryParams) => {
      try {
        setLoading(true);
        const response = await calendarClient.logs.getAll({
          query: {
            page,
            limit,
            status: 'FAILED',
            startTime,
            endTime,
            searchText,
          },
        });
        setLoading(false);

        if (response.status !== 200) {
          return errorNotification(getErrorMessage(response));
        }

        setLogs(response.body.results);
        setTotalResults(response.body.totalResults);
      } catch (error) {
        errorNotification(
          error.message || getText('integration.calendar.logs.error')
        );
        setLogs([]);
        setTotalResults(0);
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchMS365Logs({
      limit,
      page: curPage,
      startTime: dateRange[0]?.toDate(),
      endTime: dateRange[1]?.toDate(),
      searchText,
    });
  }, [curPage, limit, searchText, dateRange, fetchMS365Logs]);

  const pagination = useMemo<TablePaginationConfig>(
    () => ({
      current: curPage,
      onChange: setCurPage,
      total: totalResults,
      pageSize: limit,
      onShowSizeChange: (_current, limit) => {
        setLimit(limit);
      },
      size: 'small',
      position: ['bottomCenter'],
    }),
    [curPage, totalResults, limit]
  );

  const title = useTableTitle<Log>({
    total: totalResults,
    filteredAmount: logs.length,
  });

  return (
    <DefaultContentLayout
      title={getText('integration.calendar.logs.title')}
      subtitle={getText('integration.calendar.logs.subtitle')}
    >
      <LoadingWrapper isLoading={loading}>
        <Space direction="vertical" style={{ marginTop: 20 }}>
          <Space>
            <Typography.Text strong>Time: </Typography.Text>
            <DatePicker.RangePicker
              style={{ width: '300px' }}
              onChange={(dates) => {
                setDateRange(dates || [null, null]);
                setCurPage(1);
              }}
            />
            <TEInput.Input
              onChange={setSearchText}
              suffix={<Icon id="SearchOutlined" alt="Search" />}
              allowClear
              placeholder="Search logs"
            />
          </Space>
          <EditableTable
            columns={columns}
            dataSource={logs}
            key={'id'}
            pagination={pagination}
            rowKey={'id'}
            title={title}
            expandable={{
              expandedRowRender: (logEntry) => (
                <LogDetails logEntry={logEntry} />
              ),
              rowExpandable: (logEntry: Log) =>
                !!logEntry.context && logEntry.context.length > 0,
              expandIcon: ({ expanded, onExpand, record }) => {
                return !!record.context && record.context.length > 0 ? (
                  <RenderIcon
                    expanded={expanded}
                    onExpand={onExpand}
                    record={record}
                  />
                ) : null;
              },
            }}
          />
        </Space>
      </LoadingWrapper>
    </DefaultContentLayout>
  );
}

const columns: Column<Log>[] = [
  {
    key: 'time',
    dataIndex: 'time',
    title: 'Time',
    width: '100px',
    render: (time: string) => (
      <Typography.Text>
        {dayjs(time).format('YYYY-MM-DD HH:mm:ss')}
      </Typography.Text>
    ),
  },
  {
    key: 'message',
    dataIndex: 'message',
    ellipsis: true,
    title: 'Message',
    width: '300px',
  },
  {
    key: 'status',
    dataIndex: 'status',
    title: 'Status',
    width: '100px',
    render: (status: LogStatus) => {
      switch (status) {
        case 'SUCCESS':
          return <Tag color="green">{status}</Tag>;
        case 'FAILED':
          return <Tag color="red">{status}</Tag>;
        default:
          return status;
      }
    },
  },
];
